import { Pill, Apple, TrendingUp, Shield, FileCheck, Languages, MessageSquare, Globe, Target, Users, Clock, CheckCircle2 } from 'lucide-react';

export const mockData = {
  hero: {
    title: 'Global Regulatory Experts for LATAM, GCC, Africa & Asia',
    subtitle: 'We help Indian pharmaceutical and nutraceutical manufacturers enter high-growth international markets with clear, compliant, and submission-ready regulatory documentation.',
    primaryCTA: 'Book a Regulatory Strategy Call',
    primaryCTALink: 'https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true',
    secondaryCTA: 'Explore Countries We Serve'
  },

  whoWeHelp: [
    {
      title: 'Pharmaceutical Manufacturers',
      icon: Pill,
      color: '#606090',
      description: 'Complete CTD/ACTD dossier support, stability document planning, API/DMF coordination.'
    },
    {
      title: 'Nutraceutical Manufacturers',
      icon: Apple,
      color: '#7090A0',
      description: 'Ingredient acceptability, label/claim evaluation, country-specific supplement pathways.'
    },
    {
      title: 'Exporters & Distributors',
      icon: TrendingUp,
      color: '#A060B0',
      description: 'Multi-country portfolio expansion, submission-ready files, importer coordination.'
    }
  ],

  services: [
    {
      title: 'CTD/ACTD Dossier Preparation',
      icon: FileCheck,
      color: '#606090',
      description: 'Structured, country-ready documentation for pharma & nutra products.'
    },
    {
      title: 'API Documentation & DMF Coordination',
      icon: Shield,
      color: '#7090A0',
      description: 'Complete support for API dossiers, tech files, and manufacturer documentation.'
    },
    {
      title: 'Label, Artwork & Translation Compliance',
      icon: Languages,
      color: '#A060B0',
      description: 'Artwork alignment, bilingual designs, French/Arabic/Burmese translation checks.'
    },
    {
      title: 'Deficiency & Query Handling',
      icon: MessageSquare,
      color: '#708080',
      description: 'Clear responses to regulator queries to minimize delays.'
    },
    {
      title: 'Local Distributor Coordination',
      icon: Globe,
      color: '#606090',
      description: 'Support your importer with all required submission materials.'
    },
    {
      title: 'Add-ons',
      icon: Target,
      color: '#7090A0',
      description: 'Artwork design, eCTD formatting, technical literature compilation.'
    }
  ],

  whyTrustUs: [
    {
      title: 'Region-Deep Expertise',
      icon: Globe,
      color: '#606090',
      description: 'Focused on LATAM, GCC, Africa & Asia — markets with high potential and strict regulations.'
    },
    {
      title: 'Pharma + Nutra Dual Expertise',
      icon: Pill,
      color: '#7090A0',
      description: 'Handling both regulated pharmaceuticals and high-growth nutraceutical segments.'
    },
    {
      title: 'Founder-Led Execution',
      icon: Users,
      color: '#A060B0',
      description: 'Every file is overseen by a senior regulatory specialist.'
    },
    {
      title: 'Checklist-Driven Workflows',
      icon: CheckCircle2,
      color: '#708080',
      description: 'Step-by-step processes reduce rework and deficiencies.'
    },
    {
      title: 'Clear Communication, Fast Turnaround',
      icon: Clock,
      color: '#606090',
      description: 'No bottlenecks — structured updates and predictable timelines.'
    },
    {
      title: 'Built for Indian Manufacturers',
      icon: Target,
      color: '#7090A0',
      description: 'Designed for the challenges and documentation gaps of small & mid-sized companies.'
    }
  ],

  launchProgram: [
    {
      title: 'Phase 1',
      subtitle: 'Regulatory Strategy & Market Mapping',
      color: '#606090',
      description: 'Identify the right markets and build a clear compliance roadmap.'
    },
    {
      title: 'Phase 2',
      subtitle: 'Dossier Compilation (CTD/ACTD/API/DMF)',
      color: '#7090A0',
      description: 'Complete documentation package ready for submission.'
    },
    {
      title: 'Phase 3',
      subtitle: 'Labels, Artwork & Translations',
      color: '#A060B0',
      description: 'Country-compliant labeling and packaging materials.'
    },
    {
      title: 'Phase 4',
      subtitle: 'Deficiency Handling & Query Management',
      color: '#708080',
      description: 'Expert responses to regulatory queries and deficiencies.'
    }
  ],

  successPacks: [
    {
      name: 'Sourcing & Partnership Pack',
      slug: 'sourcing-partnership-pack',
      subtitle: 'Manufacturer & Importer Sourcing',
      color: '#A07050',
      description: 'Vetted GMP manufacturers, licensed importers, and partnership agreements'
    },
    {
      name: 'Groundwork Pack',
      slug: 'groundwork-pack',
      subtitle: 'Market Selection + Compliance Roadmap',
      color: '#606090',
      description: 'Market selection + compliance roadmap'
    },
    {
      name: 'Gap Analysis Pack',
      slug: 'gap-analysis-pack',
      subtitle: 'Module-wise Document Gap Review',
      color: '#7090A0',
      description: 'Module-wise document gap review'
    },
    {
      name: 'Registration & Dossier Pack',
      slug: 'registration-dossier-pack',
      subtitle: 'Submission-ready CTD/ACTD Documentation',
      color: '#A060B0',
      description: 'Submission-ready CTD/ACTD documentation'
    },
    {
      name: 'Lifecycle Support Pack',
      slug: 'lifecycle-pack',
      subtitle: 'Variations, Renewals, and Post-Approval Support',
      color: '#708080',
      description: 'Variations, renewals, and post-approval support'
    }
  ],

  countries: {
    'Africa': ['Kenya', 'Nigeria', 'Tanzania', 'Uganda', 'Benin', 'Senegal', 'Togo'],
    'Asia': ['Cambodia', 'Myanmar', 'Sri Lanka', 'Nepal', 'Mauritius', 'Malaysia', 'Bangladesh', 'Bhutan', 'Maldives'],
    'GCC': ['Bahrain', 'Kuwait', 'Oman', 'Qatar', 'Saudi Arabia', 'UAE'],
    'Latin America': ['Mexico', 'Colombia', 'Peru', 'Chile', 'Dominican Republic', 'Panama', 'Paraguay', 'Costa Rica', 'Uruguay']
  },

  testimonials: [
    {
      name: 'Regulatory Manager',
      role: 'Ahmedabad',
      company: 'Pharmaceutical Company',
      color: '#606090',
      quote: 'TheApostrophe helped us launch across East Africa with clean dossiers and zero complications.'
    },
    {
      name: 'Export Director',
      role: 'Mumbai',
      company: 'Nutraceutical Exports',
      color: '#7090A0',
      quote: 'Their understanding of GCC nutraceutical requirements saved us months of revision cycles.'
    },
    {
      name: 'Business Head',
      role: 'Bangalore',
      company: 'Pharma Exports Ltd',
      color: '#A060B0',
      quote: 'The GAP Analysis Pack showed us exactly what was missing. We got approvals in record time.'
    }
  ],

  blogPosts: [
    {
      slug: 'gap-analysis-saves-dossier-rejections',
      title: 'How GAP Analysis Saves You from Dossier Rejections',
      category: 'Regulatory Strategy',
      color: '#606090',
      excerpt: 'Understanding the critical role of gap analysis in ensuring dossier completeness and reducing regulatory rejections.',
      date: 'Jan 2025',
      readTime: '8 min read'
    },
    {
      slug: 'choose-right-export-markets',
      title: 'How to Choose the Right Export Markets for Your Pharma/Nutra Products',
      category: 'Market Strategy',
      color: '#7090A0',
      excerpt: 'A structured framework for evaluating and selecting the most profitable export markets for your products.',
      date: 'Jan 2025',
      readTime: '10 min read'
    },
    {
      slug: 'reduce-time-to-market-exports',
      title: 'How to Reduce Time-to-Market for Export Launches',
      category: 'Export Strategy',
      color: '#A060B0',
      excerpt: 'Four proven strategies to accelerate your regulatory approvals and reduce time-to-market across Africa and Asia.',
      date: 'Jan 2025',
      readTime: '6 min read'
    }
  ]
};